import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { first, map } from 'rxjs/operators';
import {
  Category,
  Event,
  EventCreateOptions,
  Organizer,
  Platforms,
  TicketingPlatform,
  defaultCategory,
  WiloutEvent,
  OrganizerAccessRight,
  EventStatus,
  PayoutPolicy,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class WiloutEventService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  getWiloutEvents(eventId: string) {
    const route = `${this.configUrl}w/a/wiloutEvents?eventId=${eventId}`;

    return this.http.get<{
      wiloutEvents: WiloutEvent[],
      reelaxEvents: Event[],
      organizer: Organizer,
    }>(route).pipe(
      first(),
    );
  }

  loadEvents(options: EventCreateOptions) {
    return this.getWiloutEvents(options.organizerId).pipe(
      map( (events) => events.wiloutEvents?.map((e) => this.mapWiloutToReelaxEvent(
        e,
        events.reelaxEvents,
        events.organizer,
      ))),
    );
  }

  mapWiloutToReelaxEvent(wilout: WiloutEvent, reelaxEvents: Event[], organizer: Organizer) {
    const parseCapacity = (cap: number) => (cap ? (cap > 2147483647 ? 2147483647 : cap) : undefined);
    return {
      id: reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === wilout.id.toString()),
      )?.id ?? undefined,
      name: wilout.name,
      organizerName: wilout.organizer.name,
      location: [
        wilout['address?'].city,
        wilout['address?'].country,
      ].filter((x) => !!x).join(', '),
      dateStart: new Date(wilout.date_start * 1000),
      dateEnd: wilout['date_end?'] ? new Date(wilout['date_end?'] * 1000) : undefined,
      imgUrl: wilout['img?'],
      address: [
        wilout['address?'].address1,
        wilout['address?'].address2,
        wilout['address?'].city,
        wilout['address?'].zip_code,
        wilout['address?'].state,
        wilout['address?'].country,
      ].filter((x) => !!x).join(', '),
      isPartner: true,
      allowPayinRefund: true,
      status: EventStatus.Online,
      capacity: parseCapacity(wilout['capacity?']),
      TicketingPlatforms: [new TicketingPlatform({
        platform: Platforms.Wilout,
        platformEventId: wilout.id.toString(),
        payoutPolicy: PayoutPolicy.Auto,
      })],
      Categories: wilout.categories.map( (b, i) => new Category({
        ...defaultCategory,
        available: b.price > 0,
        name: b.name,
        price: Math.round(b.price * 100),
        minimalSalePrice: Math.round(b.price * 10),
        maximalSalePrice: Math.round(b.price * 100),
        platformsForeignIds: {[Platforms.Wilout]: b.id.toString()},
        order: i,
        capacity: parseCapacity(b['capacity?']),
      })),
      PostalAddress: {
        address1: wilout['address?']?.address1,
        address2: wilout['address?']?.address2,
        city: wilout['address?']?.city,
        zip_code: wilout['address?']?.zip_code,
        state: wilout['address?']?.state,
        country: wilout['address?'].country,
      },
      Organizers: [new Organizer({
        ...organizer,
        EventOrganizer: {
          accessRight: OrganizerAccessRight.Owner,
        },
      })],
    };
  }

}
